/**
 * Normalising media tags.
 *
 * Tags are how the agent knows which assets a customer means — "use my
 * summer-event images" has to resolve to an exact set, because the model cannot
 * see the images and filenames are whatever Canva exported (usually
 * "Untitled design (3).png").
 *
 * That only works if the same campaign always produces the same string. Left
 * free-form, one campaign becomes "summer-event", "Summer Event" and
 * "summerevent" within a week, and a query for any one of them silently misses
 * the others — the worst failure mode here, because it looks like the images
 * simply are not there.
 */

/** Lowercase, trim, collapse whitespace and separators to single hyphens. */
export function normalizeTag(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Parse a free-text tag field into normalised, de-duplicated tags.
 *
 * Accepts comma or space separated input — people type both, and rejecting one
 * of them is a worse experience than accepting either.
 */
export function parseTagInput(raw: string): string[] {
  if (!raw) return [];
  const seen = new Set<string>();
  for (const part of raw.split(",")) {
    const tag = normalizeTag(part);
    if (tag) seen.add(tag);
  }
  return Array.from(seen);
}

/**
 * Tags the app writes on a customer's behalf. None of these were ever chosen by
 * a person, so none belong in a list that suggests what to type next.
 *
 * Every one is assigned in code:
 *   logo, brand-assets                     LogoUpload
 *   avatar, training, consent              avatar + consent forms
 *   pexels, video, stock                   UGC image steps
 *   edited, carousel_slide, post:<id>      ReviewPost / CreatePostComponentV2
 *   raw, generated, processed              the content pipeline
 * plus the generating provider's name, because VideoModal stores
 * `["edited", originalProvider]` — which is where a bare "gemini" comes from.
 */
const SYSTEM_TAGS = new Set([
  "logo", "brand-assets", "avatar", "training", "consent",
  "pexels", "video", "stock", "edited", "carousel_slide",
  "raw", "generated", "processed",
  // provider names written by VideoModal's originalProvider
  "gemini", "openai", "dalle", "stability", "ideogram", "heygen", "argil",
]);

/**
 * Is this tag machine-written rather than user-chosen?
 *
 * Deliberately a denylist. The alternative — marking user tags at write time —
 * cannot classify the tags already in DynamoDB, and every tag in the dev
 * library today is machine-written, so an allowlist would hide everything.
 */
export function isSystemTag(raw: string): boolean {
  const t = normalizeTag(raw);
  if (!t) return true;
  // `post:post-f64d6f20-...` — an internal id, never meaningful to a customer.
  if (t.startsWith("post:")) return true;
  return SYSTEM_TAGS.has(t);
}

/** The tags a customer actually chose, for display. */
export function userTags(tags: string[] | undefined): string[] {
  const seen = new Set<string>();
  for (const tag of tags ?? []) {
    const t = normalizeTag(tag);
    if (t && !isSystemTag(t)) seen.add(t);
  }
  return Array.from(seen).sort();
}

/**
 * Distinct user-chosen tags already in the library, to suggest rather than
 * invent new spellings of the same campaign.
 *
 * Returns [] when the library holds only machine-written tags — the caller
 * renders no suggestion list at all in that case, so the field reads as the
 * free-text box it is instead of a three-option dropdown of internals.
 */
export function collectExistingTags(items: { tags?: string[] }[]): string[] {
  const seen = new Set<string>();
  for (const item of items) {
    for (const tag of userTags(item.tags)) seen.add(tag);
  }
  return Array.from(seen).sort();
}
